import React, { useState, useMemo } from "react";
import { useStatisticsPeriodStore } from "@/store/statisticsPeriodStore";
import classNames from "classnames";
import { ChevronIcon } from "@/assets/icons";
import { useDashboardStore } from "@/store/dashboardStore";
import { getDashboardReferals } from "@/api/base";
import {
  formatApiDate,
  getPresetRange,
  startOfDay,
} from "@/components/PeriodSelector/periodDates";
import { useUserInfoStore } from "@/store/userInfoStore";
import { DashboardReferrals } from "@/types/referals";
import { Pagination } from "@/ui/Pagination/Pagination";
import { SortingBlockDashboard } from "@/components/SortingBlock/SortingBlockDashboard";
import { DEFAULT_SORT_PARAM } from "@/store/sortTypeDashboardStore";
import { FlexColumn } from "@/ui/FlexContainer/FlexContainer";
import ContainerLoader from "@/ui/ContainerLoader/ContainerLoader";
import { useStatisticsTariffStore } from "@/store/statisticsTariffStore";
import cls from "./styles.module.scss";
import { FunnelReferralRow } from "./FunnelReferralRow";

const ALL_TIME_START = new Date(2024, 0, 1);

export const Goal30Step = () => {
  const yandexId = useUserInfoStore((s) => s.yandexId);
  const dashboardInfo = useDashboardStore((s) => s.dashboardInfo);
  const selectedTariffs = useStatisticsTariffStore((s) => s.selectedTariffs);

  const mode = useStatisticsPeriodStore((s) => s.mode);
  const isCompletionMode = mode === "completion";

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [refferals, setReferrals] = useState<DashboardReferrals | null>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const preset = useStatisticsPeriodStore((s) => s.preset);
  const customRange = useStatisticsPeriodStore((s) => s.customRange);
  const today = useMemo(() => startOfDay(new Date()), []);
  const range = useMemo(
    () =>
      customRange ??
      getPresetRange(preset, today) ?? { from: ALL_TIME_START, to: today },
    [customRange, preset, today]
  );

  const [currentPage, setCurrentPage] = useState<number>(1);

  const loadData = async (
    offset: number = 0,
    sort: string = DEFAULT_SORT_PARAM
  ) => {
    setIsLoading(true);

    try {
      const res = await getDashboardReferals(
        yandexId,
        formatApiDate(range.from),
        formatApiDate(range.to),
        mode,
        "orders_5",
        offset,
        sort,
        selectedTariffs
      );
      setReferrals(res);

      setIsLoading(false);
    } catch (error) {
      console.log(error);
    }
  };

  const changePage = async (newPage: number) => {
    setIsLoading(true);

    setCurrentPage(newPage);
    const offset = (newPage - 1) * 10;
    await loadData(offset);

    setIsLoading(false);
  };

  const sortReferals = async (sortType: string) => {
    setIsLoading(true);

    const offset = (currentPage - 1) * 10;
    await loadData(offset, sortType);

    setIsLoading(false);
  };

  const handleClick = () => {
    if (isOpen) {
      setIsOpen(false);
      return;
    }

    setIsOpen(!isOpen);
    loadData();
  };

  return (
    <div className={cls.step}>
      <button
        type="button"
        data-trigger="ref.funnel.orders_30"
        className={cls.stepToggle}
        onClick={handleClick}
      >
        <div className={cls.stepColumn}>
          <div className={cls.stepHead}>
            <div className={cls.stepName}>Сделали 30 и больше заказов</div>
            <div className={cls.stepCount}>
              {dashboardInfo?.summary.orders_30.count}
            </div>
          </div>

          {!isCompletionMode && (
            <div className={cls.track}>
              <div
                className={cls.fill}
                style={{
                  width: `
                  ${dashboardInfo?.summary.orders_30.percent}%`,
                }}
              />
            </div>
          )}

          <div className={cls.stepMeta}>
            {!isCompletionMode && (
              <span className={cls.stepPercent}>
                {dashboardInfo?.summary.orders_30.percent} % от
                зарегистрированных
              </span>
            )}
            <span className={cls.stepMoney}>
              {Number(dashboardInfo?.summary.orders_30.amount).toLocaleString(
                "ru-RU"
              )}{" "}
              ₽
            </span>
          </div>
        </div>

        <div className={classNames(cls.chevron, isOpen && cls.chevronOpen)}>
          <ChevronIcon size={15} />
        </div>
      </button>

      {isOpen &&
        (isLoading || !refferals ? (
          <ContainerLoader />
        ) : (
          <FlexColumn gap={14}>
            <SortingBlockDashboard sortReferals={sortReferals} />

            <div className={cls.stepBody}>
              {refferals.items.map((referral) => (
                <FunnelReferralRow key={referral.id} referral={referral} />
              ))}
            </div>

            <Pagination
              currentPage={currentPage}
              onPageChange={changePage}
              list={refferals}
            />
          </FlexColumn>
        ))}
    </div>
  );
};
